/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { BEM, FONT_SIZE, someIncludesCaseInsensitive } from "@adapptio/lib-components";
import { ISchemaStringWithConstConstItem } from "@adapptio/lib-blueprint";
import { IFieldValidationError } from "../FieldTypes";
import { FieldWrapper } from "../FieldWrapper";
import { Icon, InputAdornment, InputButton, ListItemText, MenuItem, Select, Typography } from "../../core";
import { FieldStatus } from "../FieldStatus";

import "./FieldFontSize.scss";
import { isFunction, isNil } from "@adapptio/lib-shared";

export interface IFieldFontSizeProps {
	value: string;
	placeholder?: string;
	readOnly?: boolean;
	validationErrors?: IFieldValidationError[];
	className?: string;
	onChange?: (value: string) => void;
	grow?: boolean;
	constants?: ISchemaStringWithConstConstItem[];
	clearable?: boolean;
}

export const FieldFontSize: React.FC<IFieldFontSizeProps> = ({
	value,
	placeholder,
	readOnly,
	validationErrors,
	className,
	grow,
	onChange,
	constants,
	clearable
}) => {
	const baseClass = BEM("edt-field-font-size");

	const adornment = validationErrors?.length > 0 || clearable;

	const options = constants || [];

	const filteredOptions = useMemo(
		() =>
			value && !options.some((option) => option.value === value)
				? options.filter((option) => someIncludesCaseInsensitive(value, [ option.value ]))
				: options,
		[ value, constants ]
	);

	const getOptionSize = useCallback<(option: ISchemaStringWithConstConstItem) => string>(
		(option) => FONT_SIZE[option.value] || option.value,
		[]
	);

	const handleClearClick = useCallback(() => {
		if (isFunction(onChange)) {
			onChange(null);
		}
	}, [ onChange ]);

	return (
		<FieldWrapper type="font-size" className={className} grow={grow}>
			<Select
				value={isNil(value) ? "" : value}
				className={baseClass.toClassName()}
				placeholder={placeholder}
				popupClassName={baseClass.element("popup").toClassName()}
				onChange={onChange}
				allowCustomInput
				readOnly={readOnly}
				endAdornment={
					adornment && (
						<InputAdornment>
							{validationErrors?.length > 0 && <FieldStatus errors={validationErrors} />}
							{clearable && (
								<InputButton
									className={baseClass.element("editor-clear").toClassName()}
									onClick={handleClearClick}
									tabIndex={-1}
									disabled={!value}
								>
									<Icon
										className={baseClass.element("editor-clear-icon").toClassName()}
										name="mdi/close"
									/>
								</InputButton>
							)}
						</InputAdornment>
					)
				}
			>
				{filteredOptions?.length > 0 ? (
					filteredOptions.map((option) => (
						<MenuItem
							key={option.value}
							value={option.value}
							className={baseClass.element("option").toClassName()}
						>
							<ListItemText
								className={baseClass.element("option-text").toClassName()}
								align="left"
								primary={
									<Typography
										size={FONT_SIZE.XSMALL}
										className={baseClass.element("option-label").toClassName()}
										truncate
									>
										{option.label}
									</Typography>
								}
								secondary={
									<Typography
										size={getOptionSize(option)}
										className={baseClass.element("option-preview").toClassName()}
										preferCssValue
										noWrap
										truncate
									>
										Lorem ipsum dolor sit amet
									</Typography>
								}
							/>
						</MenuItem>
					))
				) : (
					<MenuItem disabled>No items found</MenuItem>
				)}
			</Select>
		</FieldWrapper>
	);
};
